import { Box } from "@mui/material"
import type { FC } from "react"
import { memo, useEffect } from "react"
import { useFormContext } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { Form } from "@/components/Forms"
import { FormTextField } from "@/components/UiKit"
import CompanyTypeSelect from "@/screens/Companies/components/CompanyTypeSelect"
import PreferredMeasuringSystemSelects from "@/screens/Companies/components/PreferredMeasuringSystemSelects"
import type { TreeNode } from "@/types/tree"
import { classNames } from "@/utils/index"

interface Props {
  companyTypeList: TreeNode<string>[]
  step: number
  handleCancel: () => void
  handleNextStep: () => void
  onSubmit: () => void
}

const CreateNewCompanyForm: FC<Props> = ({ companyTypeList, step, handleCancel, handleNextStep, onSubmit }) => {
  const {
    formState: { errors },
    watch,
    resetField,
    control,
  } = useFormContext()

  const companyName = watch("name")
  const types = watch("types")
  const preferredMeasurementSystem = watch("preferredMeasurementSystem")

  useEffect(() => {
    if (!companyName?.trim()) {
      resetField("description")
      resetField("types")
      resetField("accountManagers")
    }
  }, [companyName, resetField])

  useEffect(() => {
    resetField("defaultUom")
  }, [preferredMeasurementSystem, resetField])

  const isNameInvalid = !companyName?.trim() || !!errors.name
  const isStep2Invalid =
    isNameInvalid || !types?.length || !!errors.types || !!errors.description || !preferredMeasurementSystem

  const shouldShowStepTwoFields = step !== 1 && companyName?.trim()

  return (
    <Form
      className={classNames("p-10", shouldShowStepTwoFields ? "gap-6" : "gap-[36px]")}
      onSubmit={(e) => e.preventDefault()}
    >
      <Box>
        <FormTextField control={control} name='name' label='Company Name' placeholder='Company Name' required />
      </Box>
      {shouldShowStepTwoFields && (
        <>
          <FormTextField
            control={control}
            name='description'
            label='Company Description'
            placeholder='Company Description'
            data-testid='company.create-form.description-field'
          />
          <Box>
            <CompanyTypeSelect companyTypeTree={companyTypeList} name='types' />
            <PreferredMeasuringSystemSelects />
          </Box>
        </>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
        <Button appearance='outlined' onClick={handleCancel}>
          Cancel
        </Button>
        {step === 1 && (
          <Button appearance='contained' onClick={handleNextStep} disabled={isNameInvalid}>
            Continue
          </Button>
        )}
        {step === 2 && (
          <Button appearance='contained' type='submit' onClick={onSubmit} disabled={isStep2Invalid}>
            Submit
          </Button>
        )}
      </Box>
    </Form>
  )
}

export default memo(CreateNewCompanyForm)
